import {
	Anchor,
	Box,
	Card,
	Flex,
	Group,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import type { AiPromptsByMemberItem } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Selector, SelectorTarget } from '@repo/common/components/Selectors';
import { SelectType } from '@repo/common/components/Selectors/types';
import { UserAvatar } from '@repo/common/components/UserAvatar';
import { Icon, Text } from '@repo/foundations';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { useAuthUser } from '../../../api';
import {
	AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT,
	AI_PROMPTS_BY_MEMBER_SORT_OPTIONS,
} from '../constants';
import type {
	AiPromptsByMemberContentProps,
	AiPromptsByMemberListComponentProps,
	AiPromptsByMemberSortOrder,
} from '../types';
import { AiImpactWidgetEmpty } from './AiImpactWidgetEmpty';
import { AiImpactWidgetSkeleton } from './AiImpactWidgetSkeleton';
import { useStyles } from './styles';

const AiPromptsByMemberContent = memo(
	({
		items,
		sortOrder,
		onSortChange,
		theme,
		hasNextPage,
		isFetching,
		onLoadMore,
		error,
		dateFilter,
	}: AiPromptsByMemberContentProps) => {
		const navigate = useNavigate();
		const { classes } = useStyles();
		const { user, workspace } = useAuthUser();

		const handleSortChange = useCallback(
			(values: string[]) => {
				const newSortOrder = values[0] as AiPromptsByMemberSortOrder;
				if (newSortOrder && newSortOrder !== sortOrder) {
					onSortChange(newSortOrder);
				}
			},
			[onSortChange, sortOrder]
		);

		const handlePromptClick = useCallback(
			(member: AiPromptsByMemberItem) => {
				trackEvent(
					'ai_impact_dashboard/member_row_clicked',
					{
						user_id: member.user_id,
						prompt_count: member.prompt_count.toString(),
						sort_order: sortOrder,
						current_date_filter: dateFilter ?? '30d',
					},
					user,
					workspace
				);
				const filter = [
					{
						value: [member.user_id],
						operator: 'is',
						filterType: 'frequent_users',
					},
				];
				const encodedFilters = encodeURIComponent(JSON.stringify(filter));
				navigate(`/analytics/ai-chats?filters=${encodedFilters}`);
			},
			[navigate, sortOrder, dateFilter, user, workspace]
		);

		const itemContent = useCallback(
			(index: number, prompt: AiPromptsByMemberItem) => (
				<Box
					pb="md"
					className={classes.card}
					onClick={() => handlePromptClick(prompt)}
				>
					<Flex justify="space-between" align="center">
						<Flex
							justify="start"
							align="center"
							gap="sm"
							className={classes.avatarInfo}
						>
							<UserAvatar
								size="md"
								user={{
									id: prompt.user_id,
									display_name: prompt.user_name || 'Unknown user',
									first_name: '',
									last_name: '',
									profile_picture: (
										prompt as typeof prompt & {
											user_profile_picture?: string;
										}
									).user_profile_picture,
								}}
							/>
							<Stack spacing={0} className={classes.avatarInfo}>
								<Text
									size="sm"
									color="text/primary/default"
									weight="semibold"
									lineClamp={1}
								>
									{prompt.user_name || prompt.user_email}
								</Text>
								<Text size="xs" color="text/secondary/default" lineClamp={1}>
									{`${prompt.user_email} · ${prompt.user_role}`}
								</Text>
							</Stack>
						</Flex>
						<Box style={{ flexShrink: 0 }}>
							<Text size="sm" color="text/primary/default">
								{prompt.prompt_count}
							</Text>
						</Box>
					</Flex>
				</Box>
			),
			[classes.card, handlePromptClick]
		);

		if ((items.length === 0 && !isFetching) || error) {
			return (
				<AiImpactWidgetEmpty
					theme={theme}
					message={
						error
							? 'Error loading AI prompts'
							: 'No AI prompts by users found for the selected time period'
					}
					icon={error ? 'exclamationCircle' : 'users'}
				/>
			);
		}

		return (
			<Stack spacing={0}>
				<Group position="apart" pt="sm" pr="sm" pb="sm" pl="md">
					<Group>
						<Icon name="users" size="md" color="text/primary/default" />
						<Text size="sm" color="text/primary/default" fw={600}>
							Prompts by member
						</Text>
					</Group>
					<Selector
						selectType={SelectType.Single}
						initialValue={[sortOrder]}
						onChange={handleSortChange}
						getItems={async () => AI_PROMPTS_BY_MEMBER_SORT_OPTIONS}
						disableSearch
					>
						<SelectorTarget size="md" variant="default">
							{
								AI_PROMPTS_BY_MEMBER_SORT_OPTIONS.find(
									(opt) => opt.value === sortOrder
								)?.label
							}
						</SelectorTarget>
					</Selector>
				</Group>
				<Box
					mih={AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT}
					h={AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT}
				>
					<Virtuoso
						style={{ height: '100%' }}
						data={items}
						endReached={() => {
							if (hasNextPage && !isFetching) {
								onLoadMore();
							}
						}}
						itemContent={itemContent}
					/>
				</Box>
			</Stack>
		);
	}
);

export const AiPromptsByMemberList = memo(
	({
		items,
		onSortChange,
		sortOrder,
		isLoading,
		error,
		handleSeeAllClick,
		hasNextPage,
		isFetching,
		onLoadMore,
		dateFilter,
	}: AiPromptsByMemberListComponentProps) => {
		const theme = useMantineTheme();

		if (isLoading && items.length === 0) {
			return <AiImpactWidgetSkeleton halfWidth />;
		}

		return (
			<Card
				p={theme.spacing.xs}
				w="50%"
				withBorder
				style={{ borderRadius: theme.radius.md }}
			>
				<Stack>
					<AiPromptsByMemberContent
						items={items}
						sortOrder={sortOrder}
						onSortChange={onSortChange}
						theme={theme}
						hasNextPage={hasNextPage}
						isFetching={isFetching}
						onLoadMore={onLoadMore}
						error={error}
						dateFilter={dateFilter}
					/>
					<Flex justify="flex-end" pb="sm">
						<Anchor
							color={theme.other.getColor('text/emphasis/default')}
							weight="semibold"
							fz="sm"
							onClick={handleSeeAllClick}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: theme.spacing['2xs'],
							}}
						>
							<Text size="sm" color="text/emphasis/default">
								View more
							</Text>
							<Icon name="chevronRight" color="text/emphasis/default" />
						</Anchor>
					</Flex>
				</Stack>
			</Card>
		);
	}
);
